import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToasts } from '@savgroup-front-common/core/molecules/NotificationsProvider';
import { ClaimService } from '@/api';
import useGetModelById from '@/hooks/useGetModelById';
import { useGetClaimGroupStateQuery } from '@/view/app/hooks/useGetClaimGroupStateQuery';
import { useGetCustomerFileFullSummaryQuery } from '@/view/app/hooks/useGetCustomerFileFullSummaryQuery';
import useGetIssuesByOrder from '@/view/app/hooks/useGetIssuesByOrder';
import { useGetSolutionTypeQuery } from '@/view/app/hooks/useGetSolutionTypeQuery';
import {
  buildClaimReasonUrl,
  buildClaimStepUrl,
  buildFileFollowupUrl,
} from '@/view/app/NewLayout/ClassiqueLayout.helpers';

import { OwnerProductSummaryDto } from '../../HomePage.types';

export const useGetProductsDetails = ({
  product,
}: {
  product: OwnerProductSummaryDto;
}) => {
  const { pushErrors } = useToasts();
  const navigate = useNavigate();
  const { model, isLoading: isModelLoading } = useGetModelById({
    modelId: product?.modelId,
  });
  const { issues, isIssuesLoading } = useGetIssuesByOrder({
    orderId: product.orderId,
  });
  const { productsInfo } = useGetCustomerFileFullSummaryQuery({
    fileId: product.fileId,
  });
  const { solution, isLoading: isLoadingSolution } = useGetSolutionTypeQuery({
    solutionTypeId: productsInfo?.solutionId,
  });
  const { claimContextsState, isLoading: isLoadingClaimGroupState } =
    useGetClaimGroupStateQuery({
      claimGroupId: product.claimGroupId,
    });

  const handleCreateClaimWithIssue = useCallback(
    async ({
      ownerId,
      ownerProductIds,
      issueId,
    }: {
      ownerId: string;
      ownerProductIds?: string[];
      issueId: string;
    }) => {
      if (!ownerId || !ownerProductIds || !issueId) {
        return undefined;
      }

      const response = await ClaimService.setCreateClaimGroup({
        ownerId,
        ownerProductIds,
        issueId,
      });

      if (response.failure) {
        pushErrors(response.errors);

        return undefined;
      }

      const claimGroupId = response.value;

      navigate(buildClaimReasonUrl({ claimGroupId }));

      return claimGroupId;
    },
    [navigate, pushErrors],
  );

  const handleFollowClaim = useCallback(
    ({ fileId }: { fileId: string }) => {
      navigate(buildFileFollowupUrl({ fileId }));
    },
    [navigate],
  );

  const handleFinalizeClaim = useCallback(() => {
    if (product.claimGroupId && claimContextsState) {
      navigate(
        buildClaimStepUrl({
          claimContextsState,
          claimGroupId: product.claimGroupId,
        }),
      );
    }
  }, [product.claimGroupId, claimContextsState, navigate]);

  return {
    productsDetails: {
      ...product,
      ...model,
      solutionName: solution?.name,
    },
    issues,
    isLoading:
      isModelLoading ||
      isIssuesLoading ||
      isLoadingSolution ||
      isLoadingClaimGroupState,
    handleCreateClaimWithIssue,
    handleFollowClaim,
    handleFinalizeClaim,
  };
};
